import { susCalculIndicateursCodes, susFormatRpuCodes } from '@/models/sus.type';
import { Select, SimpleGrid, Stack, Switch } from '@mantine/core';
import { TimeInput } from '@mantine/dates';
import { useTranslate } from '@tolgee/react';
import { Controller, type Control } from 'react-hook-form';
import SusRequiredLabel from './SusRequiredLabel';
import type { SusFormValues } from './susFormSchema';

type Props = {
  control: Control<SusFormValues>;
  disabled: boolean;
};

/** Saisie des paramètres de transmission (les seuils sont dans `SusThresholdFields`). */
const SusSettingsFields = ({ control, disabled }: Props) => {
  const { t } = useTranslate();

  const formatRpuOptions = susFormatRpuCodes.map(code => ({
    value: code,
    label: t(`admin.sus.format_rpu.${code}`),
  }));
  const calculOptions = susCalculIndicateursCodes.map(code => ({
    value: code,
    label: t(`admin.sus.calcul_indicateurs.${code}`),
  }));

  return (
    <Stack gap="xs">
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
        <Stack gap="xs">
          <Controller
            control={control}
            name="heure_premier_envoi_quotidien"
            render={({ field, fieldState }) => (
              <TimeInput
                {...field}
                label={t('admin.sus.detail.heure_premier_envoi_quotidien')}
                error={fieldState.error ? t('admin.sus.form.error.required') : undefined}
                disabled={disabled}
                required
              />
            )}
          />
          <Controller
            control={control}
            name="calcul_automatique_indicateurs"
            render={({ field }) => (
              <Select
                ref={field.ref}
                name={field.name}
                onBlur={field.onBlur}
                value={field.value ?? null}
                onChange={value => field.onChange(value)}
                data={calculOptions}
                label={t('admin.sus.detail.calcul_automatique_indicateurs')}
                clearable
                disabled={disabled}
              />
            )}
          />
        </Stack>
        <Controller
          control={control}
          name="format_rpu"
          render={({ field, fieldState }) => (
            <Select
              ref={field.ref}
              name={field.name}
              onBlur={field.onBlur}
              value={field.value ?? null}
              onChange={value => field.onChange(value ?? undefined)}
              data={formatRpuOptions}
              label={t('admin.sus.detail.format_rpu')}
              description={t('admin.sus.form.format_rpu.description')}
              allowDeselect={false}
              error={fieldState.error ? t('admin.sus.form.error.required') : undefined}
              disabled={disabled}
              required
            />
          )}
        />
      </SimpleGrid>
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs" mt="xs">
        <Controller
          control={control}
          name="reactif_j_1"
          render={({ field }) => (
            <Switch
              ref={field.ref}
              name={field.name}
              onBlur={field.onBlur}
              checked={field.value}
              onChange={event => field.onChange(event.currentTarget.checked)}
              label={<SusRequiredLabel label={t('admin.sus.detail.reactif_j_1')} />}
              disabled={disabled}
            />
          )}
        />
        <Controller
          control={control}
          name="transmission_autorisee"
          render={({ field }) => (
            <Switch
              ref={field.ref}
              name={field.name}
              onBlur={field.onBlur}
              checked={field.value}
              onChange={event => field.onChange(event.currentTarget.checked)}
              label={<SusRequiredLabel label={t('admin.sus.detail.transmission_autorisee')} />}
              disabled={disabled}
            />
          )}
        />
      </SimpleGrid>
    </Stack>
  );
};

export default SusSettingsFields;
